import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Users, Calendar, Coffee, Sparkles } from 'lucide-react';
import { Button } from '../components/Button';
import { Card, CardContent } from '../components/Card';
import { QuoteCard } from '../components/QuoteCard';
import type { Story } from '../data/stories';
const volunteerStories: Story[] = [{
  quote: 'Vrijwilliger zijn bij De Maekerij is het beste wat ik ooit gedaan heb. Ik geef iets terug aan de gemeenschap en krijg er zoveel meer voor in de plaats.',
  name: 'Greet',
  context: 'Vrijwilliger sinds 2023',
  category: 'general',
  slug: 'greet-vrijwilliger',
  fullStory: `Na mijn pensioen zocht ik iets om mijn dagen te vullen. Een vriendin nam me mee naar De Maekerij en ik was meteen verkocht. Toen ik hoorde dat ze vrijwilligers zochten, twijfelde ik geen seconde.\n\nNu help ik elke week bij het onthaal en de koffiebar. Ik verwelkom nieuwe bezoekers, schenk koffie en maak een praatje. Het klinkt simpel, maar het betekent zoveel — voor hen én voor mij. Ik voel me nuttig, gewaardeerd en onderdeel van iets moois.`
}, {
  quote: 'Ik begon als bezoeker, werd vrijwilliger en nu help ik mee workshops organiseren. De Maekerij groeit met je mee.',
  name: 'Vera',
  context: 'Vrijwilliger workshops',
  category: 'general'
}, {
  quote: 'Het mooiste aan vrijwilligerswerk hier is dat je zelf kiest wat je doet. Er is voor iedereen een plek.',
  name: 'Isabelle',
  context: 'Vrijwilliger maekerscafés',
  category: 'general'
}];
const roles = [{
  icon: <Coffee size={24} data-id="element-1317" />,
  title: 'Onthaal & koffiebar',
  description: 'Verwelkom bezoekers, help achter de bar en zorg voor een warme sfeer.'
}, {
  icon: <Sparkles size={24} data-id="element-1318" />,
  title: 'Workshops ondersteunen',
  description: 'Help bij de voorbereiding en het opruimen van workshops, begeleid deelnemers.'
}, {
  icon: <Users size={24} data-id="element-1319" />,
  title: 'Maekerscafés begeleiden',
  description: 'Draai mee als gastvrouw bij een maekerscafé en zorg dat iedereen zich welkom voelt.'
}, {
  icon: <Calendar size={24} data-id="element-1320" />,
  title: 'Evenementen & communicatie',
  description: 'Help bij het organiseren van evenementen, sociale media of het schrijven van nieuwsbrieven.'
}];
export function Vrijwilligers() {
  return <div className="container mx-auto px-4 py-12" data-id="element-1321">
      <div className="max-w-4xl mx-auto" data-id="element-1322">
        <div className="text-center mb-12" data-id="element-1323">
          <h1 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-1324">
            Onze vrijwilligers
          </h1>
          <p className="text-xl text-gray-600" data-id="element-1325">
            Het kloppende hart achter De Maekerij
          </p>
        </div>

        {/* Intro */}
        <section className="mb-16" data-id="element-1326">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center" data-id="element-1327">
            <div data-id="element-1328">
              <p className="text-lg text-gray-600 leading-relaxed mb-4" data-id="element-1329">
                De Maekerij draait voor een groot deel op de inzet van
                vrijwilligers. Zonder hen geen gezellige maekerscafés, geen warm
                onthaal en geen soepele workshops.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed" data-id="element-1330">
                Onze vrijwilligers zijn vrouwen (en mannen!) die geloven in
                verbinding en gemeenschap. Ze geven hun tijd, energie en talent
                — en krijgen er een warm netwerk, nieuwe vaardigheden en veel
                plezier voor terug.
              </p>
            </div>
            <div className="border-2 border-dashed border-gray-300 h-64 flex items-center justify-center bg-gray-100 rounded-lg" data-id="element-1331">
              <span className="text-gray-400" data-id="element-1332">Vrijwilligers sfeerbeeld</span>
            </div>
          </div>
        </section>

        {/* What volunteers do */}
        <section className="mb-16" data-id="element-1333">
          <h2 className="text-2xl font-bold mb-8 text-center" data-id="element-1334">
            Wat doen onze vrijwilligers?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1335">
            {roles.map(role => <Card key={role.title} variant="outlined" data-id="element-1336">
                <CardContent data-id="element-1337">
                  <div className="flex items-start" data-id="element-1338">
                    <div className="border border-dashed border-gray-300 w-12 h-12 rounded-full flex items-center justify-center bg-gray-100 mr-4 flex-shrink-0" data-id="element-1339">
                      <span className="text-gray-400" data-id="element-1340">{role.icon}</span>
                    </div>
                    <div data-id="element-1341">
                      <h3 className="font-bold mb-1" data-id="element-1342">{role.title}</h3>
                      <p className="text-sm text-gray-600" data-id="element-1343">
                        {role.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>)}
          </div>
        </section>

        {/* Appreciation */}
        <section className="mb-16" data-id="element-1344">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 md:p-12" data-id="element-1345">
            <div className="flex items-start mb-6" data-id="element-1346">
              <Heart size={24} className="text-gray-400 mr-3 mt-1 flex-shrink-0" data-id="element-1347" />
              <h2 className="text-xl font-bold" data-id="element-1348">Onze waardering</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1349">
              <div data-id="element-1350">
                <h3 className="font-bold mb-2 text-sm" data-id="element-1351">Wat je terugkrijgt</h3>
                <ul className="space-y-2 text-sm text-gray-600" data-id="element-1352">
                  <li data-id="element-1353">• Een warm en hecht team van gelijkgestemden</li>
                  <li data-id="element-1354">• Gratis deelname aan workshops (als dankjewel)</li>
                  <li data-id="element-1355">• Korting in onze koffiebar</li>
                  <li data-id="element-1356">• Jaarlijks vrijwilligersfeest</li>
                  <li data-id="element-1357">• Nieuwe vaardigheden en ervaringen</li>
                </ul>
              </div>
              <div data-id="element-1358">
                <h3 className="font-bold mb-2 text-sm" data-id="element-1359">Wat we vragen</h3>
                <ul className="space-y-2 text-sm text-gray-600" data-id="element-1360">
                  <li data-id="element-1361">• Een paar uur per week of per maand — jij kiest</li>
                  <li data-id="element-1362">• Enthousiasme en een open houding</li>
                  <li data-id="element-1363">• Geen ervaring nodig, we leren je alles</li>
                  <li data-id="element-1364">• Betrouwbaarheid en een glimlach</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Volunteer stories */}
        <section className="mb-16" data-id="element-1365">
          <h2 className="text-2xl font-bold mb-6" data-id="element-1366">
            Verhalen van vrijwilligers
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1367">
            {volunteerStories.map((story, i) => <QuoteCard key={i} story={story} data-id="element-1368" />)}
          </div>
        </section>

        {/* CTA */}
        <section className="mb-8" data-id="element-1369">
          <div className="text-center bg-white border border-gray-200 rounded-lg p-8 md:p-12" data-id="element-1370">
            <h2 className="text-2xl md:text-3xl font-bold mb-4" data-id="element-1371">
              Word vrijwilliger
            </h2>
            <p className="text-gray-600 mb-8 max-w-xl mx-auto" data-id="element-1372">
              Heb je zin om mee te draaien bij De Maekerij? We zijn altijd op
              zoek naar enthousiaste mensen. Neem vrijblijvend contact op — we
              plannen graag een kennismakingsgesprek.
            </p>
            <Link to="/contact" data-id="element-1373">
              <Button variant="primary" size="large" rightIcon={<ArrowRight size={18} data-id="element-1375" />} data-id="element-1374">
                Neem contact op
              </Button>
            </Link>
          </div>
        </section>
      </div>
    </div>;
}